import Vue from 'vue';
import Proxy from '@/proxies/PaymentGenerationProxy';

export const getDefaultState = () => ({
  chargingNote: false,
  serviceId: '',
  serviceData: {
    commentEnabled: false,
    unitsEnabled: false,
    serviceDescriptionEnabled: false,
    descriptions: {},
    effortEnabled: false,
  },
  validFormValues: false,
  formValues: {},
  xtcAuthResponse: null,
  xtcBookingResponse: null,
});

export const state = getDefaultState();

export const types = {
  SET_CHARGING_NOTE: 'SET_CHARGING_NOTE',
  SET_SERVICE_ID: 'SET_SERVICE_ID',
  SET_SERVICE_DATA: 'SET_SERVICE_DATA',
  SET_FORM_VALUES: 'SET_FORM_VALUES',
  SET_XTC_AUTH_RESPONSE: 'SET_XTC_AUTH_RESPONSE',
  SET_XTC_BOOKING_RESPONSE: 'SET_XTC_BOOKING_RESPONSE',
  SET_VALID_FROM_VALUES: 'SET_VALID_FORM_VALUES',
  RESET: 'RESET',
  RESTORE: 'RESTORE',
};

export const mutations = {
  [types.SET_CHARGING_NOTE](updateState, note) {
    updateState.chargingNote = note;
    savePaymentStateToSession(updateState);
  },

  [types.SET_SERVICE_ID](updateState, serviceId) {
    updateState.serviceId = serviceId;
    savePaymentStateToSession(updateState);
  },

  [types.SET_FORM_VALUES](updateState, formValues) {
    // Object.assign(state.formValues, formValues);
    Vue.set(updateState, 'formValues', formValues);
    savePaymentStateToSession(updateState);
  },

  [types.SET_VALID_FROM_VALUES](updateState, valid) {
    updateState.validFormValues = valid;
    savePaymentStateToSession(updateState);
  },

  [types.SET_SERVICE_DATA](updateState, data) {
    Vue.set(updateState, 'serviceData', data);
    savePaymentStateToSession(updateState);
  },

  [types.SET_XTC_AUTH_RESPONSE](updateState, response) {
    Vue.set(updateState, 'xtcAuthResponse', response);
    savePaymentStateToSession(updateState);
  },

  [types.SET_XTC_BOOKING_RESPONSE](updateState, response) {
    Vue.set(updateState, 'xtcBookingResponse', response);
    savePaymentStateToSession(updateState);
  },

  [types.RESET](updateState) {
    Object.assign(updateState, getDefaultState());
    savePaymentStateToSession(updateState);
  },

  [types.RESTORE](updateState) {
    try {
      const paymentData = JSON.parse(sessionStorage.getItem('payment'));
      if (paymentData) Object.assign(updateState, paymentData);
    } catch (e) {
      // payload is invalid!
    }
  },
};

export const actions = {
  setChargingNote({ commit, dispatch }, chargingNote) {
    if (chargingNote === false) {
      dispatch('reset');
    } else {
      commit(types.SET_CHARGING_NOTE, chargingNote);
    }
  },

  fetchServiceData({ commit, state, dispatch }, serviceId) {
    if (state.chargingNote && state.serviceId !== serviceId) {
      dispatch('reset');
      commit(types.SET_CHARGING_NOTE, true);
      commit(types.SET_SERVICE_ID, serviceId);
    }
    return new Proxy().details(serviceId).then(r => commit(types.SET_SERVICE_DATA, r));
  },

  setFormValues({ commit }, formValues) {
    commit(types.SET_FORM_VALUES, formValues);
  },

  setValidForm({ commit }, valid) {
    commit(types.SET_VALID_FROM_VALUES, valid);
  },

  callXtcAuth({ commit, state }) {
    const reqObj = buildRequestObj(state);
    return new Proxy()
      .auth(reqObj)
      .then(r => {
        r.callStatus = 'SUCCESS';
        r.serviceId = state.serviceId;
        commit(types.SET_XTC_AUTH_RESPONSE, r);
      })
      .catch(function(error) {
        const r = {
          callStatus: 'ERROR',
          serviceId: state.serviceId,
          xid: error.data?.args?.xid,
          error,
          errorText: error.data?.message,
        };
        commit(types.SET_XTC_AUTH_RESPONSE, r);
      });
  },

  callXtcBook({ commit, state }) {
    const reqObj = buildRequestObj(state);
    return new Proxy()
      .book(reqObj)
      .then(r => {
        r.callStatus = 'SUCCESS';
        r.serviceId = state.serviceId;
        commit(types.RESET);
        commit(types.SET_XTC_BOOKING_RESPONSE, r);
      })
      .catch(function(error) {
        const r = {
          callStatus: 'ERROR',
          serviceId: state.serviceId,
          xid: error.data?.args?.xid,
          error,
          errorText: error.data?.message,
        };
        commit(types.RESET);
        commit(types.SET_XTC_BOOKING_RESPONSE, r);
      });
  },

  reset({ commit }) {
    commit(types.RESET);
  },

  autoInit({ commit }) {
    commit(types.RESTORE);
  },
};

const savePaymentStateToSession = currentState => sessionStorage.setItem('payment', JSON.stringify(currentState));

const buildRequestObj = currentState => {
  const reqObj = {
    serviceId: currentState.serviceId,
    descriptions: Object.keys(currentState.serviceData.descriptions).map(name => ({ name, value: currentState.formValues[name] })),
  };
  if (currentState.serviceData.effortEnabled) {
    reqObj.hours = parseInt(currentState.formValues.hours);
    reqObj.minutes = parseInt(currentState.formValues.minutes);
  }

  if (currentState.serviceData.serviceDescriptionEnabled && currentState.formValues.serviceDescription) {
    reqObj.serviceDescription = currentState.formValues.serviceDescription;
  }

  if (currentState.serviceData.commentEnabled && currentState.formValues.commentField) {
    reqObj.commentField = currentState.formValues.commentField;
  }
  if (currentState.serviceData.unitsEnabled) {
    reqObj.units = parseInt(currentState.formValues.units);
  }
  return reqObj;
};
